import { PlusIcon } from '@heroicons/react/24/outline';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import MainButton from '@/components/ui/custom/buttons/MainButton';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import ConditionalWrapper from '@/components/ui/custom/ConditionalWrapper';
import { Tooltip } from '@/components/ui/custom/dataDisplay/Tooltip';
import { useAutoFocus } from '@/data/hook/useAutoFocus';
import { StudioPermissions } from '@/enums/VersioningStatus';
import { TempChapter } from '@/models/Chapter';
import { bodyText } from '@/styles/styles';
import { useMemo } from 'react';
import { CourseStudioFormFields } from '../CourseStudio.hooks';
import LessonBookChapter from './LessonBookChapter';

type Props = {
  blockIndex: number;
  lessonIndex: number;
  studioPermissions: StudioPermissions;
  startAllOpen?: boolean;
};

export default function LessonBookChapters(props: Props) {
  const { blockIndex, lessonIndex, studioPermissions, startAllOpen } = props;

  const { t } = useTranslation('translation', {
    keyPrefix: 'courseStudio',
  });

  const { control } = useFormContext<CourseStudioFormFields>();

  const {
    fields: chapters,
    append,
    remove,
  } = useFieldArray({
    control,
    name: `lessonBlocks.${blockIndex}.lessons.${lessonIndex}.chapters`,
    keyName: 'tempId',
  });

  const disabled = useMemo(
    () =>
      !studioPermissions.allowOtherSteps.simpleEditing &&
      !studioPermissions.allowOtherSteps.advancedEditing,
    [studioPermissions],
  );

  function handleAddChapter() {
    const newChapter = new TempChapter();

    append(newChapter);
  }

  const { setElementRef } = useAutoFocus(chapters.length);

  return (
    <section className="flex flex-col gap-1">
      <label className="font-500 font-rubik">{t('chapters')}</label>
      <ConditionalRenderer
        condition={chapters.length}
        fallback={<p className={bodyText}>{t('noChapters')}</p>}
      >
        <ul className="flex flex-col gap-2.5">
          {chapters.map((chapter, chapterIndex) => (
            <LessonBookChapter
              key={chapter.id || chapter.tempId}
              blockIndex={blockIndex}
              lessonIndex={lessonIndex}
              chapterIndex={chapterIndex}
              chapter={chapter}
              studioPermissions={studioPermissions}
              removeChapter={() => remove(chapterIndex)}
              setChapterElementRef={setElementRef}
              startAllOpen={startAllOpen}
            />
          ))}
        </ul>
      </ConditionalRenderer>
      <div className="mt-auto shrink-0">
        <ConditionalWrapper
          condition={!studioPermissions.allowOtherSteps.advancedEditing}
          wrapper={<Tooltip text={t('notAllowedLimitedEditing')} />}
        >
          <MainButton
            disabled={disabled}
            color="custom"
            className="text-primary font-500 capitalize"
            icon={<PlusIcon />}
            text={t('addChapter')}
            onClick={handleAddChapter}
          />
        </ConditionalWrapper>
      </div>
    </section>
  );
}
